import { useEffect } from 'react';

/**
 * Global keyboard shortcuts hook.
 *
 * Shortcuts:
 * - N             → open Add Task dialog
 * - /             → focus search bar
 * - 1 / 2 / 3 / 4 → scroll to pocket (To Do / Planned / Doing / Done)
 * - Esc           → close any open dialog (handled by MUI)
 * - Cmd+K / Ctrl+K → open command palette
 *
 * Shortcuts are disabled when the user is typing in an input/textarea/contentEditable element,
 * except for Cmd+K/Ctrl+K and Esc which always work.
 */
const POCKET_SCROLL_IDS = {
    '1': 'pocket-to-do',
    '2': 'pocket-planned',
    '3': 'pocket-doing',
    '4': 'pocket-done'
};

const isTypingInInput = (target) => {
    if (!target) return false;
    const tag = target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (target.isContentEditable) return true;
    return false;
};

const scrollToPocket = (pocketKey) => {
    const id = POCKET_SCROLL_IDS[pocketKey];
    if (!id) return;
    const el = document.getElementById(id);
    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
};

const focusSearch = () => {
    // Try to find the search input rendered by Header/SearchBar
    const searchInput = document.querySelector('input[placeholder*="Search" i], input[aria-label*="search" i]');
    if (searchInput) {
        searchInput.focus();
        searchInput.select?.();
    }
};

const useKeyboardShortcuts = (enabled = true) => {
    useEffect(() => {
        if (!enabled) return undefined;

        const handleKeyDown = (event) => {
            const { key, metaKey, ctrlKey } = event;

            // Cmd+K / Ctrl+K → command palette (works even in inputs)
            if ((metaKey || ctrlKey) && key.toLowerCase() === 'k') {
                event.preventDefault();
                window.dispatchEvent(new CustomEvent('openCommandPalette'));
                return;
            }

            // Other shortcuts disabled while typing
            if (isTypingInInput(event.target)) return;

            // Esc handled natively by MUI Dialog, no action needed here
            if (key === 'Escape') return;

            // N → new task
            if (key.toLowerCase() === 'n' && !metaKey && !ctrlKey) {
                event.preventDefault();
                window.dispatchEvent(new CustomEvent('openAddTaskDialog', { detail: { pocket: null } }));
                return;
            }

            // / → focus search
            if (key === '/') {
                event.preventDefault();
                focusSearch();
                return;
            }

            // 1-4 → scroll to pocket
            if (['1', '2', '3', '4'].includes(key)) {
                event.preventDefault();
                scrollToPocket(key);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [enabled]);
};

export default useKeyboardShortcuts;
